import { Fragment, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import {
  Copy,
  Check,
  Loader2,
  ChevronsUpDown,
  Plus,
  FileText,
} from "lucide-react";
import { useHistory } from "react-router-dom";
import { cn } from "@/utils/classnames";
import { useQuickPayCreateDrawer } from "@/hooks/invoice/use-quickpay-create-drawer";
import { Button } from "@/components/ui/button-tw";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { CurrencyInputMasked } from "@/components/form/currency-input";
import { useForm, Controller } from "react-hook-form";
import { toast } from "react-toastify";
import { useCreateCheckoutLink } from "@/queries/checkout";
import { useAuth } from "@/providers/auth";
import { useContacts } from "@/hooks/contact/use-contacts";
import { getContactName, getContactSearchValue } from "@/utils/contact";
import { CreateContactDrawer } from "@/components/contact/create-contact-drawer";
import { useQueryClient } from "@tanstack/react-query";

export default function QuickPayDrawer() {
  const { open: drawerOpen, handleClose } = useQuickPayCreateDrawer();
  const { cognitoId, clientId, userRole } = useAuth();
  const history = useHistory();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;
  const [linkCopied, setLinkCopied] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [generatedLink, setGeneratedLink] = useState("");
  const [contactError, setContactError] = useState("");
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [addContact, setAddContact] = useState(false);

  const { data: contacts = [] } = useContacts();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      reference: "",
      amount: "",
    },
  });

  const createCheckoutLinkMutation = useCreateCheckoutLink({
    onSuccess: (data) => {
      setGeneratedLink(data.link);
      toast.success("Quick Pay link created successfully!", {
        closeButton: false,
      });
    },
    onError: (error) => {
      toast.error("Failed to create Quick Pay link. Please try again.");
      console.error("Error creating Quick Pay link:", error);
    },
  });

  const onClose = () => {
    reset();
    setSelectedContact(null);
    setLinkCopied(false);
    setGeneratedLink("");
    setContactError("");
    handleClose();
  };

  const navigateToInvoice = () => {
    const params = new URLSearchParams();

    if (selectedContact?.contact_id) {
      params.set("contact_id", selectedContact.contact_id);
    }

    const referenceValue = control._formValues.reference;
    if (referenceValue) {
      params.set("reference", referenceValue);
    }

    onClose();
    const queryString = params.toString();
    history.push(`/invoices/new${queryString ? `?${queryString}` : ""}`);
  };

  const onSubmit = (data) => {
    setContactError("");

    if (!selectedContact) {
      setContactError("Please select a contact");
      return;
    }

    if (!userId) {
      toast.error("User ID is required");
      return;
    }

    const amountString = String(data.amount || "").replace(/,/g, "");
    const amount = parseFloat(amountString);

    const contactTo = {
      email: selectedContact.contact_info?.email || "",
      first_name: selectedContact.contact_info?.first_name || "",
      last_name: selectedContact.contact_info?.last_name || "",
    };

    if (selectedContact.contact_info?.business_name) {
      contactTo.business_name = selectedContact.contact_info.business_name;
    }

    if (
      selectedContact.contact_info?.phone &&
      selectedContact.contact_info.phone.length >= 7
    ) {
      contactTo.phone_number = selectedContact.contact_info.phone;
    }

    if (selectedContact.contact_id) {
      contactTo.external_identifier = selectedContact.contact_id;
    }

    if (selectedContact.contact_info?.business_address) {
      const info = selectedContact.contact_info;

      contactTo.billing_address = {
        billing_address_line1: info.business_address || "",
        billing_address_line2: "",
        billing_city: info.business_city || "",
        billing_state: info.business_state || "",
        billing_zip_code: info.business_zip || "",
      };
    }

    const payload = {
      is_static_link: false,
      checkout: {
        user_id: userId,
        total: amount,
        contact_to: contactTo,
        payment_authorization_type: "ONE_TIME",
        payment_cadence: "INSTANT",
        ...(data.reference && {
          reference: {
            reference_type: "INVOICE",
            external_identifier: data.reference,
          },
        }),
        metadata: {},
      },
    };

    createCheckoutLinkMutation.mutate({
      userId,
      payload,
    });
  };

  const onError = (errors) => {
    console.log("Form validation errors:", errors);
  };

  const handleCopyLink = () => {
    if (generatedLink) {
      navigator.clipboard.writeText(generatedLink);
      setLinkCopied(true);
      toast.success("Link copied to clipboard!", {
        closeButton: false,
      });
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  return (
    <Transition.Root show={drawerOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <div className="fixed inset-0" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="relative pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col overflow-y-scroll bg-white shadow-xl">
                    <div className="px-4 py-6 sm:px-6 bg-skin-primary">
                      <div className="flex items-start justify-between">
                        <div>
                          <Dialog.Title className="text-base font-semibold leading-6 text-white">
                            Create QuickPay
                          </Dialog.Title>
                          <p className="mt-1 text-sm text-white/80">
                            Generate a personalized link to send to your
                            customer to request payment.
                          </p>
                        </div>
                        <div className="ml-3 flex h-7 items-center">
                          <button
                            type="button"
                            className="relative rounded-md text-gray-50 hover:text-gray-300 focus:ring-2 focus:ring-white"
                            onClick={onClose}
                          >
                            <span className="absolute -inset-2.5" />
                            <span className="sr-only">Close panel</span>
                            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="flex-1">
                      <form
                        onSubmit={handleSubmit(onSubmit, onError)}
                        className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-6"
                      >
                        <div className="space-y-8">
                          {generatedLink && (
                            <div>
                              <Label className="text-base font-semibold text-gray-900">
                                Quick Pay Link
                              </Label>
                              <div className="mt-4">
                                <div className="flex items-center justify-between p-4 border border-green-300 rounded-lg bg-green-50">
                                  <div className="flex items-center gap-3 flex-1 min-w-0">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100 flex-shrink-0">
                                      <Check className="h-5 w-5 text-green-600" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <div className="text-sm font-medium text-gray-900">
                                        Link Generated Successfully
                                      </div>
                                      <div className="text-xs text-gray-600 truncate">
                                        {generatedLink}
                                      </div>
                                    </div>
                                  </div>
                                  <Button
                                    type="button"
                                    variant={linkCopied ? "default" : "outline"}
                                    size="sm"
                                    onClick={handleCopyLink}
                                    className={cn(
                                      "ml-3 flex-shrink-0",
                                      linkCopied &&
                                        "bg-green-600 hover:bg-green-600/80"
                                    )}
                                  >
                                    {linkCopied ? (
                                      <>
                                        <Check className="h-4 w-4 mr-1" />
                                        Copied
                                      </>
                                    ) : (
                                      <>
                                        <Copy className="h-4 w-4 mr-1" />
                                        Copy
                                      </>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}

                          <div>
                            <div className="flex justify-between items-center mb-2">
                              <Label
                                htmlFor="contact"
                                className="text-base font-semibold text-gray-900"
                              >
                                Select your contact{" "}
                                <span className="text-red-500">*</span>
                              </Label>
                              <button
                                type="button"
                                className="flex justify-between items-center text-sm font-medium text-skin-primary"
                                onClick={() => setAddContact(true)}
                              >
                                <Plus className="w-4 h-4 mr-2" /> Add Contact
                              </button>
                            </div>
                            <div className="mt-4">
                              <Popover
                                open={popoverOpen}
                                onOpenChange={setPopoverOpen}
                              >
                                <PopoverTrigger asChild>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    aria-expanded={popoverOpen}
                                    className={cn(
                                      "w-full justify-between h-20 hover:bg-gray-50",
                                      {
                                        "border-l-4 border-l-skin-primary bg-gray-50":
                                          selectedContact,
                                        "border-red-500": contactError,
                                      }
                                    )}
                                  >
                                    {selectedContact?.contact_id ? (
                                      <div className="flex flex-col items-start">
                                        <span className="truncate text-base">
                                          {getContactName(selectedContact)}
                                        </span>
                                        <span className="text-sm text-gray-500 truncate">
                                          {selectedContact.contact_info
                                            ?.email || ""}
                                        </span>
                                      </div>
                                    ) : (
                                      "Select contact..."
                                    )}
                                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-full p-0 popover-content-width-same-as-its-trigger max-h-[300px] overflow-y-auto">
                                  <Command>
                                    <CommandInput placeholder="Search contact..." />
                                    <CommandEmpty>
                                      No contact found.
                                    </CommandEmpty>
                                    <CommandGroup>
                                      {contacts.map((contact) => (
                                        <CommandItem
                                          key={contact.contact_id}
                                          value={getContactSearchValue(contact)}
                                          onSelect={() => {
                                            setSelectedContact(contact);
                                            setContactError("");
                                            setPopoverOpen(false);
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4",
                                              selectedContact?.contact_id ===
                                                contact?.contact_id
                                                ? "opacity-100"
                                                : "opacity-0"
                                            )}
                                          />
                                          <span className="text-nowrap">
                                            {getContactName(contact)}
                                          </span>
                                          {contact?.contact_info?.email ? (
                                            <span className="text-xs text-gray-500 ml-3 truncate">
                                              {contact.contact_info?.email ||
                                                ""}
                                            </span>
                                          ) : (
                                            <span className="text-xs text-red-500 ml-3">
                                              You can't select this contact.
                                              Please add an email address to
                                              continue.
                                            </span>
                                          )}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                              {contactError && (
                                <p className="mt-1 text-sm text-red-600">
                                  {contactError}
                                </p>
                              )}
                            </div>
                          </div>

                          <div>
                            <Label
                              htmlFor="reference"
                              className="text-base font-semibold text-gray-900"
                            >
                              Add a Reference Number{" "}
                              <span className="text-gray-500 text-sm font-normal">
                                (Optional)
                              </span>
                            </Label>
                            <div className="mt-4">
                              <Controller
                                name="reference"
                                control={control}
                                render={({ field }) => (
                                  <Input
                                    {...field}
                                    id="reference"
                                    type="text"
                                    placeholder="e.g., Invoice #1234"
                                    className={cn(
                                      errors.reference && "border-red-500"
                                    )}
                                  />
                                )}
                              />
                              {errors.reference && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.reference.message}
                                </p>
                              )}
                            </div>
                          </div>

                          <div>
                            <Label
                              htmlFor="amount"
                              className="text-base font-semibold text-gray-900"
                            >
                              Enter the Payment Amount{" "}
                              <span className="text-red-500">*</span>
                            </Label>
                            <div className="mt-4">
                              <Controller
                                name="amount"
                                control={control}
                                rules={{
                                  required: "Amount is required",
                                  validate: (value) => {
                                    // Convert to string if needed and remove commas
                                    const stringValue = String(
                                      value || ""
                                    ).replace(/,/g, "");
                                    const numValue = parseFloat(stringValue);
                                    if (isNaN(numValue) || numValue <= 0) {
                                      return "Please enter a valid amount";
                                    }
                                    return true;
                                  },
                                }}
                                render={({ field }) => (
                                  <CurrencyInputMasked
                                    {...field}
                                    id="amount"
                                    placeholder="0.00"
                                    className={cn(
                                      errors.amount && "border-red-500"
                                    )}
                                  />
                                )}
                              />
                              {errors.amount && (
                                <p className="mt-1 text-sm text-red-600">
                                  {errors.amount.message}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Footer Actions */}
                        <div className="mt-8 flex justify-end gap-3 pt-6 border-t">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={onClose}
                          >
                            {generatedLink ? "Close" : "Cancel"}
                          </Button>
                          {!generatedLink && (
                            <Button
                              type="submit"
                              className="bg-green-600 hover:bg-green-600/80 text-white"
                              disabled={createCheckoutLinkMutation.isPending}
                            >
                              {createCheckoutLinkMutation.isPending ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Generating...
                                </>
                              ) : (
                                "Generate Link"
                              )}
                            </Button>
                          )}
                        </div>
                      </form>

                      {/* Invoice Creation Suggestion */}
                      <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 pb-6">
                        <div
                          className="flex items-center gap-4 p-4 border border-gray-300 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
                          onClick={navigateToInvoice}
                        >
                          <div className="flex-shrink-0">
                            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-skin-primary/10">
                              <FileText className="h-6 w-6 text-skin-primary" />
                            </div>
                          </div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-700">
                              Need to add a little more info like due dates or
                              line items?{" "}
                              <span className="text-skin-primary font-medium">
                                Click here
                              </span>{" "}
                              to create an invoice.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
      <CreateContactDrawer
        open={addContact}
        handleClose={() => setAddContact(false)}
        onSuccess={(contact) => {
          queryClient.invalidateQueries(["contacts"]);
          setSelectedContact(contact);
          setContactError("");
        }}
      />
    </Transition.Root>
  );
}
